import React from 'react';
import { motion } from 'framer-motion';
import { FiCpu, FiShield, FiZap, FiTarget, FiChevronRight, FiCheck } from 'react-icons/fi';
import { LandingNavbar } from '../components/Landing/LandingNavbar';

export const LandingPage = ({ onLogin }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2 } }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 overflow-x-hidden selection:bg-blue-500/30">
      <LandingNavbar onLogin={onLogin} />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-48 md:pb-32 px-6 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-blue-600/10 rounded-full blur-[120px] -z-10"></div>
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-purple-600/5 rounded-full blur-[100px] -z-10"></div>

        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="max-w-6xl mx-auto text-center"
        >
          <motion.div variants={itemVariants} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-bold tracking-widest uppercase mb-8">
            <FiZap className="animate-pulse" /> The Future of Fiber Optics
          </motion.div>
          
          <motion.h1 variants={itemVariants} className="text-5xl md:text-8xl font-black text-white mb-8 leading-[0.9] tracking-tighter">
            ELITE SUPPORT <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-blue-500 to-indigo-600">FOR EVERY SPLICER.</span>
          </motion.h1>

          <motion.p variants={itemVariants} className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto mb-12 leading-relaxed">
            AdamAI combines proprietary neural diagnostics with gamified field training to turn beginners into master technicians in record time.
          </motion.p>

          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button 
              onClick={onLogin}
              className="w-full sm:w-auto px-10 py-4 bg-white text-slate-950 font-black rounded-xl hover:bg-blue-50 transition-all hover:scale-105 active:scale-95 flex items-center justify-center gap-2 shadow-xl"
            >
              GET STARTED NOW <FiChevronRight />
            </button>
            <button className="w-full sm:w-auto px-10 py-4 bg-slate-900 text-white font-bold rounded-xl border border-white/10 hover:bg-slate-800 transition-all">
              WATCH DEMO
            </button>
          </motion.div>
        </motion.div>

        {/* Floating App Preview */}
        <motion.div 
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.8, duration: 1 }}
          className="mt-20 max-w-5xl mx-auto relative"
        >
          <div className="absolute inset-0 bg-blue-500/20 blur-[100px] rounded-full"></div>
          <div className="relative glass-panel rounded-3xl p-2 border-white/10 shadow-2xl overflow-hidden">
            <img 
              src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=1200&auto=format&fit=crop" 
              className="rounded-2xl w-full h-auto object-cover opacity-80" 
              alt="AdamAI Interface" 
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent"></div>
          </div>
        </motion.div>
      </section>

      {/* Proprietary Tech Section */}
      <section id="tech" className="py-24 px-6 bg-slate-900/50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-blue-500 font-bold tracking-widest uppercase text-sm mb-4">Neural Fiber Diagnostics™</h2>
              <h3 className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight">Proprietary AdamAI <br /> Core Technology.</h3>
              <p className="text-slate-400 mb-8 leading-relaxed">
                Unlike generic AI, AdamAI is trained on millions of high-resolution fiber end-face scans, OTDR traces, and real-world fault reports. It doesn't just guess—it identifies physical anomalies at the core level.
              </p>
              
              <div className="space-y-4">
                {[
                  'Computer Vision for end-face contamination detection',
                  'Predictive analysis for micro-bend signal loss',
                  'Real-time supervisor neural sync for complex cases',
                  'Automated compliance reporting with 99.8% accuracy'
                ].map((feature, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 shrink-0">
                      <FiCheck size={14} />
                    </div>
                    <span className="text-slate-300 font-medium">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4 pt-12">
                <div className="glass-panel p-6 rounded-2xl border-white/5 bg-blue-500/5">
                  <FiTarget className="text-blue-400 text-3xl mb-4" />
                  <h4 className="text-white font-bold mb-2">94% Confidence</h4>
                  <p className="text-xs text-slate-400">Average diagnostic precision in field conditions.</p>
                </div>
                <div className="glass-panel p-6 rounded-2xl border-white/5 bg-purple-500/5">
                  <FiCpu className="text-purple-400 text-3xl mb-4" />
                  <h4 className="text-white font-bold mb-2">On-Edge Logic</h4>
                  <p className="text-xs text-slate-400">Works in basements and remote locations without signal.</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="glass-panel p-6 rounded-2xl border-white/5 bg-emerald-500/5">
                  <FiShield className="text-emerald-400 text-3xl mb-4" />
                  <h4 className="text-white font-bold mb-2">Zero-Risk</h4>
                  <p className="text-xs text-slate-400">Safety protocols integrated into every recommendation.</p>
                </div>
                <div className="glass-panel p-6 rounded-2xl border-white/5 bg-blue-600/10 h-64 flex items-center justify-center">
                   <div className="w-12 h-12 bg-white/10 rounded-full animate-ping"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Grid */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-black text-white mb-4 tracking-tight">One Platform. Total Field Dominance.</h2>
          <p className="text-slate-400 max-w-2xl mx-auto">Everything a modern fiber technician needs to stay ahead of the curve.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <div className="glass-panel p-8 rounded-3xl border-white/5 transition-all hover:border-blue-500/30 group">
            <div className="w-14 h-14 bg-blue-500/10 text-blue-400 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <FiTarget className="text-2xl" />
            </div>
            <h4 className="text-xl font-bold text-white mb-3">Field Assist AI</h4>
            <p className="text-slate-400 text-sm leading-relaxed">Upload a photo, get a fix. Our AI diagnoses dirty connectors, bad splices, and cable damage in seconds.</p>
          </div>

          <div className="glass-panel p-8 rounded-3xl border-white/5 transition-all hover:border-blue-500/30 group">
            <div className="w-14 h-14 bg-indigo-500/10 text-indigo-400 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <FiZap className="text-2xl" />
            </div>
            <h4 className="text-xl font-bold text-white mb-3">Academy Tracks</h4>
            <p className="text-slate-400 text-sm leading-relaxed">Gamified 5-minute modules designed for mobile devices. Earn certifications as you learn in the field.</p>
          </div>

          <div className="glass-panel p-8 rounded-3xl border-white/5 transition-all hover:border-blue-500/30 group">
            <div className="w-14 h-14 bg-emerald-500/10 text-emerald-400 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
              <FiCpu className="text-2xl" />
            </div>
            <h4 className="text-xl font-bold text-white mb-3">Supervisor Sync</h4>
            <p className="text-slate-400 text-sm leading-relaxed">Instant video escalation to Tier 3 leads when things get complex. Share your data stream in real-time.</p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-blue-600/5 -z-10"></div>
        <div className="max-w-4xl mx-auto glass-panel p-12 rounded-[40px] border-white/10 text-center relative">
          <div className="absolute -top-12 left-1/2 -translate-x-1/2 w-24 h-24 bg-blue-600 rounded-3xl flex items-center justify-center shadow-2xl rotate-12">
            <FiCpu className="text-white text-4xl" />
          </div>
          
          <h2 className="text-4xl md:text-6xl font-black text-white mb-6 mt-6 leading-tight">Ready to upgrade <br /> your field skills?</h2>
          <p className="text-slate-400 mb-10 text-lg">Join 15,000+ technicians using AdamAI to solve complex fiber issues every day.</p>
          
          <button 
            onClick={onLogin}
            className="px-12 py-5 bg-blue-600 hover:bg-blue-500 text-white font-black rounded-2xl transition-all hover:scale-105 active:scale-95 shadow-2xl shadow-blue-600/40 text-lg"
          >
            LOGIN TO DASHBOARD
          </button>
        </div>
      </section>

      <footer className="py-12 px-12 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-6 text-slate-500 text-sm font-medium">
        <div className="flex items-center gap-2">
          <FiCpu className="text-blue-500" />
          <span>© 2026 AdamAI Systems Inc.</span>
        </div>
        <div className="flex gap-8">
          <a href="#" className="hover:text-white transition-colors">Privacy</a>
          <a href="#" className="hover:text-white transition-colors">Terms</a>
          <a href="#" className="hover:text-white transition-colors">Contact</a>
        </div>
      </footer>
    </div>
  );
};